
import { useState, useEffect } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { Loader2, PlusCircle, Filter, List, Calendar as CalendarIcon, Linkedin } from 'lucide-react';
import { MockBadge } from '@/components/ui/mock-badge';
import { LinkedInPostsList } from '@/components/linkedin/LinkedInPostsList';
import { ViewStateManager } from '@/state/view/ViewStateManager';
import { startOfMonth, endOfMonth, addDays } from 'date-fns';
import { useContentCalendar } from '@/hooks/useContentCalendar';
import { ContentCalendarView } from '@/components/content-calendar/ContentCalendarView';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { LinkedInConnect } from '@/components/linkedin/LinkedInConnect';
import { useLinkedInConnection } from '@/hooks/useLinkedInConnection';

const LinkedInManagement = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const viewStateManager = ViewStateManager.getInstance();
  
  // Load saved state from ViewStateManager if available
  const savedState = viewStateManager.getViewState("/dashboard/linkedin") || {};
  
  const [selectedTab, setSelectedTab] = useState(savedState.selectedTab || 'posts');
  const [viewMode, setViewMode] = useState<"list" | "month" | "week" | "day">(savedState.viewMode || "month");
  const [currentDate, setCurrentDate] = useState<Date>(savedState.currentDate ? new Date(savedState.currentDate) : new Date());
  
  const canManageLinkedIn = hasPermission('manage:linkedin');
  const { connection, isConnected, isLoading: connectionLoading } = useLinkedInConnection();

  // Save state changes to ViewStateManager
  useEffect(() => {
    viewStateManager.updateViewState("/dashboard/linkedin", {
      selectedTab,
      viewMode,
      currentDate: currentDate.toISOString()
    });
  }, [selectedTab, viewMode, currentDate]);
  
  // Set date range to the current month plus 15 days before and after
  const startDate = addDays(startOfMonth(currentDate), -15);
  const endDate = addDays(endOfMonth(currentDate), 15);
  
  // Fetch only LinkedIn posts
  const { data, isLoading } = useContentCalendar({
    startDate,
    endDate,
    contentType: 'linkedin',
    filters: {}
  });

  // Handle view mode change in calendar view
  const handleViewModeChange = (mode: "list" | "month" | "week" | "day") => {
    setViewMode(mode);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">LinkedIn Management</h1>
          <p className="text-muted-foreground">
            Create and manage your LinkedIn content
          </p>
        </div>
        <MockBadge feature="LinkedIn Management" phase="2" />
      </div>

      {!isConnected && !connectionLoading && (
        <Alert className="bg-muted border-primary/20">
          <Linkedin className="h-4 w-4" />
          <AlertTitle>LinkedIn Connection Required</AlertTitle>
          <AlertDescription className="flex flex-col gap-3">
            <p>Connect your LinkedIn account to publish posts directly from this dashboard.</p>
            <LinkedInConnect />
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <Tabs 
          defaultValue="posts" 
          className="w-full" 
          value={selectedTab} 
          onValueChange={setSelectedTab}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <TabsList>
              <TabsTrigger value="posts" className="flex items-center gap-2">
                <List size={16} />
                <span>Posts</span>
              </TabsTrigger>
              <TabsTrigger value="calendar" className="flex items-center gap-2">
                <CalendarIcon size={16} />
                <span>Calendar</span>
              </TabsTrigger>
            </TabsList>
            
            <div className="flex gap-2">
              {!isConnected && (
                <LinkedInConnect size="sm" variant="outline" />
              )}
              
              {canManageLinkedIn && (
                <Button 
                  onClick={() => navigate("/dashboard/linkedin/posts/new")} 
                  className="flex items-center gap-2"
                  disabled={!isConnected}
                >
                  <PlusCircle size={16} />
                  <span>New Post</span>
                </Button>
              )}
            </div>
          </div>

          <TabsContent value="posts" className="mt-0">
            <LinkedInPostsList />
          </TabsContent>
          
          <TabsContent value="calendar" className="mt-0">
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className="h-6 w-6 animate-spin text-primary/50" />
              </div>
            ) : data ? (
              <ContentCalendarView 
                items={data.items}
                currentDate={currentDate}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                onNewContent={() => navigate("/dashboard/linkedin/posts/new")}
              />
            ) : (
              <div className="flex items-center justify-center p-12 text-muted-foreground">
                No LinkedIn posts found for the selected period
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default LinkedInManagement;
